import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, timekeeping } from '@cwos/api-client';
import { ErrorPanel, Pill, Refused, ScopeNote, SuccessEmpty } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { authorityGate } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { ListState } from '../ListStates.js';
import { listPhase, nameResolver } from '../listState.js';
import { scopeNoteFor } from '../readScope.js';
import { SettingsField } from '../settings/common.js';
import { formatClock, formatDay } from './timeLogic.js';
import {
  canSubmitCorrection,
  CORRECTION_FILTER_LABELS,
  CORRECTION_FILTERS,
  correctionPayload,
  correctionProblem,
  correctionsEmptyCopy,
  correctionsHeading,
  correctionsPopulation,
  correctionsQuery,
  correctionsQueryKey,
  decisionFailure,
  decisionOutcome,
  describeAdjustment,
  describeAdjustmentStatus,
  EMPTY_CORRECTION_DRAFT,
  filingFailure,
  invalidateAfterDecision,
  isDecidable,
  CORRECTIONS_DECIDE,
  CORRECTIONS_READ,
  mayDecideCorrections,
  mayFileCorrections,
  mayReadCorrections,
  mayWithdrawCorrection,
  withdrawFailure,
  type CorrectionDraft,
  type CorrectionFilter,
  type DecisionFailure,
} from './corrections.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * TIME CORRECTIONS — the queue, and the form.
 *
 * The two halves of the only sanctioned way a time record changes. Both routes
 * shipped with Task 5; neither had a caller anywhere in any UI, so a punch
 * recorded at the wrong hour stayed wrong and payroll ran on it.
 *
 * WHERE THIS LIVES. `/time/approvals` already carried a read-only "Time
 * corrections waiting on a decision" list — the queue with no verb. Rather than
 * add a fifth tab, the verb went where the list already was: the same screen a
 * supervisor opens to decide timesheets and mileage is the one they open to
 * decide corrections, and all three now decide.
 *
 * The filing half sits under the queue rather than on its own screen, because
 * the overwhelmingly common way an office user files one is having just read the
 * request or the exception that made it necessary.
 */

/** How far back the session picker looks. Long enough to cover a pay period. */
const SESSION_WINDOW_DAYS = 45;

export function TimeCorrections() {
  const permissions = usePermissions();
  const { has } = permissions;
  /**
   * THE READER'S TWO DOORS, EACH WITH FOUR ANSWERS AND NOT TWO.
   *
   * `mayReadCorrections`/`mayDecideCorrections` are default-deny, which is the
   * right answer for the `enabled:` below and the wrong one for the two
   * sentences this screen used to render off them — "corrections are not shown
   * to you" and "you can see these corrections but not decide them" are both
   * accusations, and `false` here also means the permission set could not be
   * re-read. That state is REACHABLE on this screen and not theoretical:
   * `/time/approvals` is gated on `timesheet:approve`, so a reader whose cached
   * set carries that key walks through `RequirePermission` while `isError` is
   * true, and every OTHER key on the page is then answered from a set nobody
   * could refresh. `authorityGate` keeps the two apart; the booleans below stay
   * exactly as permissive as they were for the query flags and the row props.
   */
  const readGate = authorityGate(permissions, CORRECTIONS_READ);
  const decideGate = authorityGate(permissions, CORRECTIONS_DECIDE);
  const canRead = mayReadCorrections(has);
  const canDecide = mayDecideCorrections(has);
  const canFile = mayFileCorrections(has);
  /**
   * The roster, asked at organization scope because the roster route decides it
   * there: `guard(..., 'directory:read')` and `mayReadWorkerIdentity` for
   * `user:read` are both `can()` with no scope argument, and the handler 403s
   * rather than returning a shorter list.
   *
   * `rosterWithheld` is the half that may be said out loud. `canListWorkers` is
   * default-deny, so it is also false while the permission set is loading and
   * after it fails — and "reading who works here is a permission your account
   * does not hold" is a claim about the account, not about the request.
   */
  const canListWorkers = mayListWorkers(permissions);
  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canListWorkers;

  /**
   * WHICH CORRECTIONS THIS QUEUE IS SHOWING.
   *
   * `pending` is the default because deciding is what an office user opens this
   * screen to do — and it was, until now, the ONLY value: the single call site
   * of `timekeeping.adjustments()` hard-coded it, so a correction that had been
   * approved, denied or withdrawn left this screen forever and could not be
   * looked up from anywhere in the product. The route has always taken the
   * filter and the SQL has always applied it; nothing asked.
   *
   * It is a SERVER filter, not a client one. The route narrows in the query
   * (`($2::text is null or status = $2)`) and the same scope clause runs either
   * way, so switching it asks a new question rather than hiding rows already on
   * the page — which is also why each filter carries its own query key and its
   * own empty sentence.
   */
  const [filter, setFilter] = useState<CorrectionFilter>('pending');

  const requests = useQuery({
    queryKey: correctionsQueryKey(filter),
    queryFn: () => timekeeping.adjustments(correctionsQuery(filter)),
    enabled: canRead,
  });

  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canListWorkers,
  });
  // Secondary read. A failed roster fetch must not relabel every worker on the
  // queue as somebody the reader is not allowed to see — see `nameResolver`.
  const nameOf = useMemo(() => nameResolver(workers, canListWorkers), [workers, canListWorkers]);
  const scopeNote = scopeNoteFor(correctionsPopulation(permissions));

  const rows = requests.data ?? [];

  return (
    <div className="rn-col">
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">{correctionsHeading(filter)}</h2>
          {/* Offered only to a reader who may read the queue at all. A filter
              over a refusal would suggest that picking a different one might
              produce rows. */}
          {canRead ? (
            <label className="cmp-filter">
              <span className="rn-muted">Show</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as CorrectionFilter)}
                aria-label="Filter time corrections by status"
              >
                {CORRECTION_FILTERS.map((key) => (
                  <option key={key} value={key}>
                    {CORRECTION_FILTER_LABELS[key]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          Approving a correction rewrites the record: it replaces the session and its punches, and
          the hours that flow into payroll change with it. Denying one leaves the record as it
          stands.
        </p>

        {/* WHOSE QUEUE THIS IS. The unfiltered read narrows in the SQL rather
            than refusing, so a supervisor without `timekeeping:read_all` is
            looking at their own team's corrections under a heading that reads
            as the organization's — and "No corrections pending" would be a
            claim about everybody. See `correctionsPopulation`. Rendered ABOVE
            the list so it frames the empty state too. */}
        {canRead && scopeNote ? <ScopeNote scope={scopeNote} /> : null}

        {canRead ? (
          <ListState
            phase={listPhase(requests, rows.length)}
            rows={3}
            onRetry={() => void requests.refetch()}
            failedMessage="We couldn’t load pending time corrections just now. This is not the same as there being none — a correction may still be waiting."
            refused={
              <Refused
                heading="Corrections are not shown to you"
                message="Your account cannot read the correction queue, so nothing is listed here. It is withheld, not empty."
              />
            }
            empty={
              // A MEASURED ZERO, AND IT SAYS WHICH ONE. "No corrections
              // pending" under the Denied filter would be a true sentence
              // about a set the reader did not ask about, which is the same
              // collapse this screen already refuses between empty and
              // withheld, one level further down.
              <SuccessEmpty {...correctionsEmptyCopy(filter)} />
            }
          >
            <ul className="tc-rows">
              {rows.map((request) => (
                <CorrectionRow
                  key={request.id}
                  request={request}
                  workerName={nameOf(request.workerId)}
                  // BOTH HALVES. The permission answers for the reader; the
                  // status answers for the row, and the row's half could not
                  // be wrong while `pending` was the only reachable filter.
                  // `decideAdjustmentRequest` updates `where ... and status =
                  // 'pending'`, so a Decide control on a decided correction is
                  // a button whose only outcome is a 409.
                  canDecide={canDecide && isDecidable(request)}
                  // WITHDRAW IS NOT A DECISION and is not offered by the same
                  // key. It is offered to the person who FILED this one, which
                  // is a fact about the row and the reader, not about a
                  // permission — see `mayWithdrawCorrection`.
                  canWithdraw={mayWithdrawCorrection(request, permissions.userId, has)}
                />
              ))}
            </ul>
          </ListState>
        ) : (
          /* NOT SHOWN TO YOU, COULD NOT BE CHECKED, OR STILL BEING CHECKED —
             three answers where there was one. The refusal keeps its own
             words, because "this is not an empty queue" is the withheld/empty
             distinction this screen was careful about and is still the right
             thing to say to a reader who genuinely does not hold the key. */
          <GateNotice
            gate={readGate}
            what="read the correction queue"
            onRetry={() => permissions.refetch()}
            refusedNode={
              <Refused
                heading="Corrections are not shown to you"
                message="Reading the correction queue needs the timekeeping read permission. This is not an empty queue — it is one you have not been given."
              />
            }
          />
        )}

        {/* Said out loud rather than by the absence of buttons: a supervisor who
            can SEE the queue and cannot act on it should know which of the two
            they are, instead of wondering where the controls went. The
            unreadable answer is now its own sentence rather than being worded
            as a refusal — a reader whose set could not be re-read is not
            somebody who "can see these and not decide them", they are somebody
            nobody has been able to ask. */}
        {canRead ? (
          <GateNotice
            gate={decideGate}
            what="decide these corrections"
            onRetry={() => permissions.refetch()}
            refusedNode={
              <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
                You can see these corrections but not decide them. Approving or denying one needs{' '}
                <code>timekeeping:manage</code>.
              </p>
            }
          />
        ) : null}
      </section>

      {canFile ? (
        <FileCorrection
          canListWorkers={canListWorkers}
          rosterWithheld={rosterWithheld}
          workers={workers}
        />
      ) : null}
    </div>
  );
}

/**
 * One pending correction, and — for a caller holding the key — the decision.
 *
 * THE CONTROLS ARE SHOWN ON THE PERMISSION AND NOTHING ELSE. Two further gates
 * apply server-side per request: reach over the subject worker, and separation
 * of duties. Neither is derivable from the caller's own permission set — the
 * first needs the `worker.supervisor_id` edge, the second needs to know which
 * worker row is linked to the caller's account — and F35 is the open finding
 * about what happens when this app derives server scope anyway. So the refusal
 * is rendered when the server gives it, on the request it was about.
 */
function CorrectionRow({
  request,
  workerName,
  canDecide,
  canWithdraw,
}: {
  request: timekeeping.AdjustmentRequest;
  workerName: string;
  canDecide: boolean;
  /** This reader filed this pending correction and may take it back. */
  canWithdraw: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState<DecisionFailure | null>(null);
  /**
   * WHICH DECISION FAILED, so the retry repeats THAT one.
   *
   * `onRetry={() => decide.mutate('approve')}` was the first shape of this and
   * it was wrong in the worst available direction: a supervisor whose DENIAL hit
   * a 500 would press the retry button under "we couldn't record that decision"
   * and approve the correction instead — rewriting the worker's hours by
   * pressing a button labelled "try again".
   */
  const [attempted, setAttempted] = useState<'approve' | 'deny'>('approve');

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'deny') =>
      timekeeping.decideAdjustment(request.id, decisionOutcome(decision, note)),
    onMutate: (decision: 'approve' | 'deny') => {
      setAttempted(decision);
      setFailure(null);
    },
    onSuccess: () => {
      setOpen(false);
      setNote('');
      // The queue, the sessions, the timesheets and the board — approving is a
      // write to all four. See CORRECTION_DECISION_INVALIDATES.
      invalidateAfterDecision(qc);
    },
    onError: (err: unknown) => setFailure(decisionFailure(err)),
  });

  /**
   * WITHDRAWING WHAT YOU FILED — the fourth status, which nothing could write.
   *
   * `cancelled` has been in `tar_status_ck` since the table was created and no
   * route reached it, so a correction filed by mistake could only be got rid of
   * by asking somebody else to DENY it: a recorded refusal of a claim that had
   * already been abandoned, with a reviewer's name against it.
   *
   * ITS OWN FAILURE STATE, not `failure` above. The decision drawer's error
   * belongs to approve/deny; a withdrawal that failed while the drawer was
   * closed would otherwise render nowhere, and one that failed while it was
   * open would read as a failed approval.
   */
  const [withdrawFail, setWithdrawFail] = useState<DecisionFailure | null>(null);
  const withdraw = useMutation({
    mutationFn: () => timekeeping.cancelAdjustment(request.id),
    onMutate: () => setWithdrawFail(null),
    onSuccess: () => {
      // Same family as a decision: the row leaves the pending queue and appears
      // under Withdrawn, and both lists are under the same key prefix.
      invalidateAfterDecision(qc);
    },
    onError: (err: unknown) => setWithdrawFail(withdrawFailure(err)),
  });

  const status = describeAdjustmentStatus(request.status);

  return (
    <li className="tc-row" style={{ display: 'block' }}>
      <div className="tc-row-btn" style={{ cursor: 'default' }}>
        <span className="tc-row-main">
          <span className="tc-row-name">{describeAdjustment(request.requestType)}</span>
          <span className="rn-muted">
            {workerName}
            {request.requestedClockInAt
              ? ` · in ${formatClock(request.requestedClockInAt)} on ${formatDay(request.requestedClockInAt)}`
              : ''}
            {request.requestedClockOutAt
              ? ` · out ${formatClock(request.requestedClockOutAt)} on ${formatDay(request.requestedClockOutAt)}`
              : ''}
          </span>
          <span className="rn-muted">{request.reason}</span>
          {/* WHAT WAS DECIDED, on a row that has been.
              A decided correction is only reachable at all now that the filter
              exists, and reaching it to be told nothing but the word "Denied"
              would be a poor trade for a reviewer trying to answer "why".
              `decisionNote` is optional on both branches server-side, so its
              absence is an ABSENCE — nobody wrote one — and never a withholding
              this screen would have to word differently. It says so rather than
              rendering an empty line, which reads as a note that was blank. */}
          {!isDecidable(request) ? (
            <span className="rn-muted">
              {describeAdjustmentStatus(request.status).word}
              {request.reviewedAt ? ` on ${formatDay(request.reviewedAt)}` : ''}
              {' · '}
              {request.decisionNote?.trim()
                ? request.decisionNote
                : 'no note was left by the reviewer'}
            </span>
          ) : null}
        </span>
        <span className="tc-row-side">
          <Pill variant={status.tone} dot>
            {status.word}
          </Pill>
          {canDecide ? (
            <button
              type="button"
              className="rn-btn-quiet"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? 'Cancel' : 'Decide'}
            </button>
          ) : null}
          {canWithdraw ? (
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={withdraw.isPending}
              onClick={() => withdraw.mutate()}
            >
              {withdraw.isPending ? 'Withdrawing…' : 'Withdraw'}
            </button>
          ) : null}
        </span>
      </div>

      {/* Outside the decision drawer, because a withdrawal is not a decision
          and the drawer may well be closed. */}
      {withdrawFail ? (
        <div style={{ padding: 'var(--space-12)' }}>
          {withdrawFail.kind === 'refused' ? (
            <Refused heading="This one cannot be withdrawn" message={withdrawFail.message} />
          ) : (
            <ErrorPanel message={withdrawFail.message} onRetry={() => withdraw.mutate()} />
          )}
        </div>
      ) : null}

      {open && canDecide ? (
        <div style={{ padding: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
          <SettingsField
            label="Note (optional)"
            hint="Stored on the correction and the only place a reviewer can say why. A denial without one leaves the worker with no explanation."
          >
            <textarea
              style={{ marginBottom: 0, minHeight: 72 }}
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
            />
          </SettingsField>

          {/* A 403 here is an ANSWER — you do not reach this worker, or this
              correction is about you. It gets no retry. Anything else did fail
              and says so. */}
          {failure ? (
            failure.kind === 'refused' ? (
              <Refused heading="This one is not yours to decide" message={failure.message} />
            ) : (
              <ErrorPanel
                message={`${failure.message} Trying again repeats the ${
                  attempted === 'approve' ? 'approval' : 'denial'
                }.`}
                onRetry={() => decide.mutate(attempted)}
              />
            )
          ) : null}

          {/* PENDING NAMES THE DECISION IT IS ACTUALLY MAKING.
              `decide.isPending` describes the mutation, not the decision, and
              both buttons read it. So pressing "Deny" put "Recording…" on the
              APPROVE button — the one labelled "Approve and rewrite the
              record" — while the denial was in flight, and left Deny greyed out
              with its resting word. The screen said, in the most load-bearing
              place available, that the opposite decision was being taken about
              a worker's hours. `decide.variables` is the decision that was
              actually dispatched, so each button now speaks only for itself. */}
          <div className="tc-drawer-actions">
            <button
              type="button"
              className="signin-devbtn"
              style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
              disabled={decide.isPending}
              onClick={() => decide.mutate('approve')}
            >
              {decide.isPending && decide.variables === 'approve'
                ? 'Recording the approval…'
                : 'Approve and rewrite the record'}
            </button>
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={decide.isPending}
              onClick={() => decide.mutate('deny')}
            >
              {decide.isPending && decide.variables === 'deny' ? 'Recording the denial…' : 'Deny'}
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/**
 * FILING A CORRECTION ON A WORKER'S BEHALF.
 *
 * The API permits this outright, and says so where the rule is enforced:
 * `resolveSubjectUserId` (apps/api/src/domains/timekeeping/routes.ts:295) —
 * "The subject is NOT the requester: an admin or supervisor may file a
 * correction on a worker's behalf". `POST /timekeeping/adjustments` needs
 * `time:adjust` plus reach over the named worker: the supervisor edge, or
 * `timekeeping:write_all`, which org_admin holds organization-wide
 * (1756940000000_timekeeping-write-all.sql:95).
 *
 * That reach is NOT re-derived here — it depends on `worker.supervisor_id`,
 * which this app has no honest way to evaluate. The form is offered on the
 * permission, and a caller outside their reach meets the server's 403, rendered
 * as a refusal.
 *
 * The office user does not thereby decide their own request: separation of
 * duties is about the SUBJECT worker, not the filer, so an administrator filing
 * for a worker may also approve it — which is the point, and is what makes this
 * a complete loop without the mobile app.
 */
function FileCorrection({
  canListWorkers,
  rosterWithheld,
  workers,
}: {
  /** The roster query fired. Default-deny, so also false while unresolved. */
  canListWorkers: boolean;
  /**
   * A RESOLVED permission set says the two roster keys are not held over the
   * organization. The only state that licenses the refusal sentence — "not yet
   * known" is not a fact about this account.
   */
  rosterWithheld: boolean;
  workers: ReturnType<typeof useQuery<directory.Worker[]>>;
}) {
  const qc = useQueryClient();
  const [openForm, setOpenForm] = useState(false);
  const [draft, setDraft] = useState<CorrectionDraft>(EMPTY_CORRECTION_DRAFT);
  const [failure, setFailure] = useState<DecisionFailure | null>(null);
  const [filed, setFiled] = useState(false);

  const since = useMemo(
    () => new Date(Date.now() - SESSION_WINDOW_DAYS * 86_400_000).toISOString(),
    [],
  );
  // The session this correction is about. Every type but `add_missing_session`
  // names one, and a UUID typed by hand is not a workable way to name it.
  const needsSession = draft.requestType !== 'add_missing_session';
  const sessions = useQuery({
    queryKey: ['timekeeping', 'sessions', draft.workerId, since],
    queryFn: () => timekeeping.workerSessions(draft.workerId, { since }),
    enabled: Boolean(draft.workerId) && needsSession,
  });

  const file = useMutation({
    mutationFn: () => timekeeping.requestAdjustment(correctionPayload(draft)),
    onMutate: () => {
      setFailure(null);
      setFiled(false);
    },
    onSuccess: () => {
      setDraft(EMPTY_CORRECTION_DRAFT);
      setOpenForm(false);
      setFiled(true);
      invalidateAfterDecision(qc);
    },
    onError: (err: unknown) => setFailure(filingFailure(err)),
  });

  const problem = correctionProblem(draft);
  const workerRows = workers.data ?? [];

  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">File a correction</h2>
        <button
          type="button"
          className="rn-btn-quiet"
          aria-expanded={openForm}
          onClick={() => {
            setOpenForm((o) => !o);
            setFailure(null);
            setFiled(false);
          }}
        >
          {openForm ? 'Cancel' : 'File a correction'}
        </button>
      </div>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        A correction filed here is a request, not an edit — it joins the queue above and somebody
        else decides it. Filing one for a worker is ordinary: their record is wrong and they may not
        be able to reach a phone to say so.
      </p>

      {filed ? (
        <p className="rn-muted" role="status" style={{ marginTop: 'var(--space-8)' }}>
          Filed. It is waiting on a decision in the queue above.
        </p>
      ) : null}

      {openForm ? (
        <form
          className="org-create"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmitCorrection(draft)) file.mutate();
          }}
        >
          {rosterWithheld ? (
            // Withheld, not absent: an empty picker would read as "there are no
            // workers". The roster needs `directory:read` AND `user:read`, each
            // over the organization.
            <Refused
              heading="The staff roster is not shown to you"
              message="Filing on somebody's behalf means naming them, and reading who works here is a permission your account does not hold."
            />
          ) : !canListWorkers || workers.isLoading ? (
            // Not withheld and not fetched: the permission set has not resolved,
            // so the query is still disabled. Pending, not denied.
            <p className="rn-muted">Loading the roster…</p>
          ) : workers.isError && !workers.data ? (
            <LoadFailure
              error={workers.error}
              message="We couldn’t load the staff roster, so there is nobody to choose from yet. This is not an empty roster."
              onRetry={() => void workers.refetch()}
            />
          ) : (
            <div className="org-create-grid">
              <SettingsField label="Whose time">
                <select
                  style={{ marginBottom: 0 }}
                  value={draft.workerId}
                  onChange={(e) =>
                    setDraft({ ...draft, workerId: e.target.value, timeSessionId: '' })
                  }
                >
                  <option value="">Choose a worker…</option>
                  {workerRows.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.displayName}
                    </option>
                  ))}
                </select>
              </SettingsField>

              <SettingsField label="What needs correcting">
                <select
                  style={{ marginBottom: 0 }}
                  value={draft.requestType}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      requestType: e.target.value as CorrectionDraft['requestType'],
                    })
                  }
                >
                  <option value="edit_times">Change the times on a session</option>
                  <option value="add_missing_session">Add a session that was never recorded</option>
                  <option value="add_missing_punch">
                    Add a clock event that was never recorded
                  </option>
                  <option value="void_session">Void a session recorded in error</option>
                </select>
              </SettingsField>
            </div>
          )}

          {canListWorkers && needsSession && draft.workerId ? (
            <SettingsField
              label="Which session"
              hint={`Sessions clocked in over the last ${SESSION_WINDOW_DAYS} days.`}
            >
              {sessions.isLoading ? (
                <select style={{ marginBottom: 0 }} disabled>
                  <option>Loading sessions…</option>
                </select>
              ) : sessions.isError ? (
                // A failed or refused session read is NOT "this worker has no
                // sessions". An empty picker under that heading would be a
                // claim about their week.
                <select style={{ marginBottom: 0 }} disabled>
                  <option>
                    {sessions.data === undefined
                      ? 'Sessions could not be read — not the same as none'
                      : 'Sessions could not be refreshed'}
                  </option>
                </select>
              ) : (
                <select
                  style={{ marginBottom: 0 }}
                  value={draft.timeSessionId}
                  onChange={(e) => setDraft({ ...draft, timeSessionId: e.target.value })}
                >
                  <option value="">Choose a session…</option>
                  {(sessions.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {formatDay(s.clockedInAt)} · {formatClock(s.clockedInAt)} –{' '}
                      {s.clockedOutAt ? formatClock(s.clockedOutAt) : 'still open'}
                    </option>
                  ))}
                </select>
              )}
            </SettingsField>
          ) : null}

          {draft.requestType !== 'void_session' ? (
            <div className="org-create-grid">
              <SettingsField
                label={
                  draft.requestType === 'add_missing_punch'
                    ? 'Clock-in instant'
                    : 'Corrected clock-in'
                }
              >
                <input
                  type="datetime-local"

                  style={{ marginBottom: 0 }}
                  value={draft.requestedClockInAt}
                  onChange={(e) => setDraft({ ...draft, requestedClockInAt: e.target.value })}
                />
              </SettingsField>
              <SettingsField
                label={
                  draft.requestType === 'add_missing_punch'
                    ? 'Clock-out instant'
                    : 'Corrected clock-out'
                }
              >
                <input
                  type="datetime-local"

                  style={{ marginBottom: 0 }}
                  value={draft.requestedClockOutAt}
                  onChange={(e) => setDraft({ ...draft, requestedClockOutAt: e.target.value })}
                />
              </SettingsField>
            </div>
          ) : null}

          <SettingsField
            label="Why"
            hint="Required. Whoever decides this reads it, and so does anyone auditing the change later."
          >
            <textarea
              style={{ marginBottom: 0, minHeight: 72 }}
              value={draft.reason}
              maxLength={2000}
              onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            />
          </SettingsField>

          {failure ? (
            failure.kind === 'refused' ? (
              <Refused heading="Not yours to file" message={failure.message} />
            ) : (
              <ErrorPanel message={failure.message} onRetry={() => file.mutate()} />
            )
          ) : null}

          {problem && draft.workerId ? (
            <p className="rn-muted" style={{ fontSize: 13 }}>
              {problem}
            </p>
          ) : null}

          <div className="set-actions">
            <button
              type="submit"
              className="signin-devbtn"
              style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
              disabled={!canSubmitCorrection(draft) || file.isPending}
            >
              {file.isPending ? 'Filing…' : 'File the correction'}
            </button>
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => {
                setOpenForm(false);
                setDraft(EMPTY_CORRECTION_DRAFT);
                setFailure(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
